(() => {
  const ranges = (offset, limit, available, unavailable) => {
    const end = Math.min(offset + limit, available + unavailable);
    return [
      { bucket: '1', start: offset, end: Math.min(end, available) },
      { bucket: '0', start: Math.max(0, offset - available), end: end - available }
    ].filter(range => range.end > range.start);
  };

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { ranges };
    return;
  }
  if (customElements.get('pr-market-collection')) return;

  const productKey = card => {
    const link = card.querySelector('.product-card__title[href]');
    if (!link) throw new Error('Missing product identity');
    const url = new URL(link.getAttribute('href'), window.location.origin);
    const match = url.pathname.match(/\/products\/([^/]+)/);
    if (!match) throw new Error('Invalid product identity');
    return match[1];
  };

  customElements.define('pr-market-collection', class extends HTMLElement {
    connectedCallback() {
      if (this.dataset.active === 'true' || this.dataset.supported !== 'true') return;
      this.grid = this.querySelector('motion-list');
      if (!this.grid || !this.grid.querySelector('.product-card')) return;
      this.size = Number(this.dataset.size);
      if (!Number.isInteger(this.size) || this.size < 1) return;
      this.controller = new AbortController();
      this.pages = new Map();
      this.base = new URL(window.location.href);
      this.base.hash = '';
      this.page = Math.max(1, Number(this.base.searchParams.get('page')) || 1);
      this.initialPagination = this.querySelector('.pagination');
      this.initialPagination?.remove();
      this.initialize().catch(error => this.fail(error));
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
    }

    async fetchPage(bucket, page) {
      const key = `${bucket}:${page}`;
      if (this.pages.has(key)) return this.pages.get(key);
      const request = (async () => {
        const url = new URL(this.base);
        url.searchParams.set('filter.v.availability', bucket);
        url.searchParams.set('page', String(page));
        url.searchParams.set('section_id', this.dataset.section);
        const response = await fetch(url, { signal: this.controller.signal, credentials: 'same-origin' });
        if (!response.ok) throw new Error('Collection request failed');
        if (new URL(response.url).pathname !== this.base.pathname) throw new Error('Market URL changed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const result = doc.querySelector('pr-market-collection');
        if (!result || result.dataset.country !== this.dataset.country || result.dataset.language !== this.dataset.language) {
          throw new Error('Collection market context mismatch');
        }
        if (result.dataset.active !== 'true' || result.dataset.supported !== 'true') throw new Error('Availability filter unavailable');
        const total = Number(result.dataset.items);
        if (!Number.isInteger(total) || total < 0) throw new Error('Invalid collection count');
        const cards = [...result.querySelectorAll('motion-list > .product-card')];
        const expected = Math.max(0, Math.min(this.size, total - (page - 1) * this.size));
        if (cards.length !== expected) throw new Error('Incomplete collection page');
        return { total, cards };
      })();
      this.pages.set(key, request);
      try { return await request; }
      catch (error) { this.pages.delete(key); throw error; }
    }

    async initialize() {
      const [available, unavailable] = await Promise.all([this.fetchPage('1', 1), this.fetchPage('0', 1)]);
      this.total = Number(this.dataset.items);
      if (!Number.isInteger(this.total) || this.total < available.total || available.total + unavailable.total < this.total) {
        throw new Error('Availability coverage changed');
      }
      this.availableCards = [];
      this.seenProducts = new Set();
      const availablePages = Math.ceil(available.total / this.size);
      for (let first = 1; first <= availablePages; first += 3) {
        const batch = await Promise.all(Array.from({ length: Math.min(3, availablePages - first + 1) }, (_, index) => this.fetchPage('1', first + index)));
        for (const result of batch) {
          if (result.total !== available.total) throw new Error('Inventory changed during pagination');
          for (const card of result.cards) {
            const key = productKey(card);
            if (this.seenProducts.has(key)) throw new Error('Duplicate in-stock product');
            this.seenProducts.add(key);
            this.availableCards.push(card);
          }
        }
      }
      this.unavailableCards = [];
      this.unavailableTotal = unavailable.total;
      this.unavailablePage = 0;
      this.lastPage = Math.max(1, Math.ceil(this.total / this.size));
      this.page = Math.min(this.page, this.lastPage);
      const cards = await this.getCards(this.page);
      if (!this.isConnected || this.controller.signal.aborted) return;
      this.originalCards = [...this.grid.children];
      this.grid.replaceChildren(...cards);
      this.dataset.state = 'ready';
      this.grid.reload?.();
      this.renderPagination();
    }

    async getCards(page) {
      const start = (page - 1) * this.size;
      const end = Math.min(start + this.size, this.total);
      const needed = Math.max(0, end - this.availableCards.length);
      const lastUnavailablePage = Math.ceil(this.unavailableTotal / this.size);
      while ((this.unavailableCards.length < needed || page === this.lastPage) && this.unavailablePage < lastUnavailablePage) {
        const next = this.unavailablePage + 1;
        const result = await this.fetchPage('0', next);
        if (result.total !== this.unavailableTotal) throw new Error('Inventory changed during pagination');
        const additions = [];
        const keys = new Set();
        for (const card of result.cards) {
          const key = productKey(card);
          if (!this.seenProducts.has(key) && !keys.has(key)) {
            keys.add(key);
            additions.push(card);
          }
        }
        additions.forEach(card => this.unavailableCards.push(card));
        keys.forEach(key => this.seenProducts.add(key));
        this.unavailablePage = next;
      }
      const cards = [...this.availableCards, ...this.unavailableCards];
      if (cards.length > this.total || cards.length < end ||
          (this.unavailablePage === lastUnavailablePage && cards.length !== this.total && needed > 0)) {
        throw new Error('Unique product coverage changed');
      }
      return cards.slice(start, end).map(card => card.cloneNode(true));
    }

    renderPagination() {
      this.observer?.disconnect();
      this.controls?.remove();
      if (this.page >= this.lastPage && this.dataset.mode !== 'paginated') return;
      this.controls = document.createElement('div');
      this.controls.className = 'pagination flex flex-wrap justify-center align-self-center md:gap-2 text-sm md:text-base';
      this.controls.dataset.marketPagination = 'true';
      this.append(this.controls);
      if (this.dataset.mode === 'paginated') {
        if (this.page > 1) this.addLink(this.page - 1, this.dataset.previous);
        for (let page = Math.max(1, this.page - 2); page <= Math.min(this.lastPage, this.page + 2); page++) {
          this.addLink(page, String(page), page === this.page);
        }
        if (this.page < this.lastPage) this.addLink(this.page + 1, this.dataset.next);
        return;
      }
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'button button--primary button--fixed';
      button.textContent = this.dataset.more;
      button.addEventListener('click', () => this.loadMore(button));
      this.controls.append(button);
      if (this.dataset.mode === 'infinite') {
        this.observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) this.loadMore(button);
        }, { rootMargin: '200px 0px' });
        this.observer.observe(button);
      }
    }

    addLink(page, label, current = false) {
      const link = document.createElement(current ? 'span' : 'a');
      link.className = 'page flex items-center justify-center rounded-full';
      link.textContent = label;
      if (current) link.setAttribute('aria-current', 'page');
      else {
        const url = new URL(this.base);
        url.searchParams.set('page', String(page));
        url.searchParams.delete('section_id');
        link.href = url.toString();
      }
      this.controls.append(link);
    }

    async loadMore(button) {
      if (this.loading || this.page >= this.lastPage) return;
      this.loading = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      try {
        const cards = await this.getCards(this.page + 1);
        if (!this.isConnected || this.controller.signal.aborted) return;
        this.grid.append(...cards);
        this.page++;
        this.grid.reload?.();
        this.renderPagination();
      } catch (error) {
        if (error.name !== 'AbortError') {
          this.observer?.disconnect();
          button.disabled = false;
          button.removeAttribute('aria-busy');
          this.dataset.state = 'retry';
          console.warn('Market collection loading paused', error.message);
        }
      } finally { this.loading = false; }
    }

    fail(error) {
      if (error.name === 'AbortError' || !this.isConnected) return;
      this.dataset.state = 'fallback';
      if (this.originalCards) this.grid.replaceChildren(...this.originalCards);
      if (this.initialPagination && !this.initialPagination.isConnected) this.append(this.initialPagination);
      console.warn('Market sorting retained the original collection', error.message);
    }
  });
})();
